"use client";

import React, { useRef, useEffect } from "react";
import { useGlobalChat } from "../contexts/ChatContext";
import ChatHeader from "./ChatHeader";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import ChatToggleButton from "./ChatToggleButton";

interface ChatbotProps {
  config?: {
    headerTitle?: string;
    logo?: string;
    position?: {
      placement?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
      offsetX?: string;
      offsetY?: string;
    };
    width?: string;
    maxHeight?: string;
    disableAutoScroll?: boolean;
  };
}

const Chatbot: React.FC<ChatbotProps> = ({ config = {} }) => {
  // Use the global chat context instead of direct hook
  const { isOpen, isMinimized, messages } = useGlobalChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (!config.disableAutoScroll && messagesEndRef.current && !isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMinimized, config.disableAutoScroll]);

  if (!isOpen) {
    return <ChatToggleButton config={config} />;
  }

  // Style variables based on position
  const positionClasses = (() => {
    const position = config.position?.placement || "bottom-right";

    switch (position) {
      case "top-left":
        return "top-5 left-5";
      case "top-right":
        return "top-5 right-5";
      case "bottom-left":
        return "bottom-5 left-5";
      case "bottom-right":
      default:
        return "bottom-5 right-5";
    }
  })();

  return (
    <div
      className={`fixed ${positionClasses} z-50 bg-white rounded-lg shadow-xl overflow-hidden flex flex-col aspire-chat-container`}
      style={{
        width: config.width || "350px",
        maxWidth: "90vw",
        maxHeight: config.maxHeight || "600px",
        minHeight: isMinimized ? "60px" : "300px",
      }}
      role="dialog"
      aria-label="Chat window"
    >
      <ChatHeader title={config.headerTitle} logo={config.logo} />

      {!isMinimized && (
        <>
          <div
            className="flex-1 overflow-y-auto p-4 flex flex-col gap-3"
            role="log"
            aria-live="polite"
          >
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </div>
          <ChatInput />
        </>
      )}
    </div>
  );
};

export default Chatbot;
